"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowDown,
  ArrowRight,
  Check,
  Copy,
  Lock,
  Minus,
  Plus,
} from "lucide-react";

import { ContactFormDialog } from "@/components/contact-form-dialog";
import { MODEL_LICENSE_1_0 } from "@/components/license/model-license-1.0";
import { cn } from "@/lib/utils";

const serif = "font-[Source_Serif_4,Georgia,serif]";

const clauseText = `You may not provide Moondream to third parties as a hosted
or managed service where that service gives users access to
general-purpose Moondream inference or fine-tuning.

A narrow, domain-specific application built on Moondream that
does not expose general-purpose model functionality is not a
hosted or managed service under this license, and is permitted.`;

const usageRows = [
  { use: "Internal or self-hosted use", status: "Allowed", tone: "allowed" },
  {
    use: "Commercial applications and SaaS features",
    status: "Allowed",
    tone: "allowed",
  },
  {
    use: "Fine-tuning, quantization, conversion, and redistribution",
    status: "Allowed",
    tone: "allowed",
  },
  {
    use: "Edge devices and customer-controlled VPCs",
    status: "Allowed",
    tone: "allowed",
  },
  {
    use: "Customer operates Moondream on leased cloud or neocloud GPUs",
    status: "Allowed",
    tone: "allowed",
  },
  {
    use: "Domain-specific application powered by Moondream",
    status: "Allowed",
    tone: "allowed",
  },
  {
    use: "Narrow vertical API that does not expose general-purpose model functionality",
    status: "Allowed",
    tone: "allowed",
  },
  {
    use: "Provider-operated general-purpose Moondream inference API",
    status: "Separate license required",
    tone: "required",
  },
  {
    use: "Hosted general-purpose fine-tuning service",
    status: "Separate license required",
    tone: "required",
  },
  {
    use: "Shared API exposing a fine-tuned or quantized Moondream derivative",
    status: "Separate license required",
    tone: "required",
  },
] as const;

const faqs = [
  {
    q: "Why does the license text look familiar?",
    a: "Because the hosting restriction follows the Elastic License 2.0, a widely used source-available license. We took its managed-service clause and narrowed it to general-purpose hosted inference or fine-tuning, rather than writing new language from scratch. Your lawyer will recognize the structure and can review a known restriction instead of novel wording.",
  },
  {
    q: "I run Moondream on rented or neocloud GPUs. Is that hosting?",
    a: "No. Running Moondream for your own use on infrastructure you lease is allowed, the same as running it on hardware you own. The restriction is about offering Moondream itself to third parties as a general-purpose service, not about where your servers physically live.",
  },
  {
    q: "What counts as general-purpose?",
    a: "Giving users the model's full functionality (detect, query, caption, point, segment) through arbitrary prompts, as the thing being offered. A narrow vertical API that does one domain task, and does not expose general-purpose model functionality, is a product built on Moondream, not a Moondream service.",
  },
  {
    q: "Can I fine-tune and ship the result?",
    a: "Yes. Fine-tune, quantize, convert, and redistribute freely. The restriction applies again only if you turn that derivative into a shared API that exposes general-purpose Moondream functionality to third parties.",
  },
  {
    q: "How do I get the separate license?",
    a: "Contact us with a short description of the service you want to run. The commercial hosting license is straightforward to scope.",
  },
] as const;

function StatusPill({
  tone,
  children,
}: {
  tone: "allowed" | "required";
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-[28px] items-center gap-2 self-start whitespace-nowrap rounded-full border px-3 text-[12px] font-semibold leading-none sm:self-center",
        tone === "allowed"
          ? "border-[#16A34A]/30 bg-[#16A34A]/10 text-[#15803D] dark:text-[#4ADE80]"
          : "border-[#D97706]/30 bg-[#D97706]/10 text-[#B45309] dark:text-[#FBBF24]",
      )}
    >
      <span
        className={cn(
          "size-1.5 rounded-full",
          tone === "allowed" ? "bg-[#16A34A]" : "bg-[#D97706]",
        )}
      />
      {children}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-5 flex items-center gap-3">
      <span className="t-eyebrow text-muted-foreground">{children}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

export function LicensePageContent() {
  const [copied, setCopied] = useState(false);
  const [openFaqs, setOpenFaqs] = useState<Record<number, boolean>>({
    0: true,
  });
  const [contactOpen, setContactOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const copyClause = async () => {
    try {
      await navigator.clipboard.writeText(clauseText);
    } catch {
      /* Clipboard access can be unavailable in some browser contexts. */
    }

    setCopied(true);
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <>
      <main>
        <section className="mx-auto max-w-[880px] px-8 pt-24 max-md:px-6 max-md:pt-16">
          <div className="inline-flex h-7 items-center gap-2 rounded-full border bg-card px-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" />
            License
          </div>
          <h1
            className={cn(
              serif,
              "mt-6 max-w-[760px] text-balance text-[60px] font-medium leading-[64px] tracking-[-1.6px] max-md:text-[46px] max-md:leading-[52px] max-sm:text-[38px] max-sm:leading-[44px]",
            )}
          >
            The Moondream License.
          </h1>
          <p className="mt-5 max-w-[690px] text-[19px] leading-[30px] text-muted-foreground max-sm:text-[17px] max-sm:leading-[27px]">
            A short, source-available license with one restriction. You can use
            Moondream in your products, fine-tune it, and run it on your own
            hardware. The one thing you cannot do is offer general-purpose
            Moondream itself as a hosted service.
          </p>
        </section>

        <section className="mx-auto max-w-[1080px] px-8 pt-14 max-md:px-6">
          <SectionLabel>The short version</SectionLabel>

          <div className="mx-auto max-w-[760px] overflow-hidden rounded-2xl border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-6 p-10 max-sm:p-6">
              <span
                className={cn(
                  serif,
                  "text-[24px] font-medium leading-8 tracking-[-0.4px] max-sm:text-[22px]",
                )}
              >
                Is Moondream a <em>component</em> of your product?
              </span>
              <span className="inline-flex h-[38px] items-center gap-2 rounded-full border border-[#16A34A]/30 bg-[#16A34A]/10 px-[18px] text-[16px] font-semibold leading-none text-[#15803D] dark:text-[#4ADE80]">
                <Check className="size-4" strokeWidth={3} />
                Allowed
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-6 border-t p-10 max-sm:p-6">
              <span
                className={cn(
                  serif,
                  "text-[24px] font-medium leading-8 tracking-[-0.4px] max-sm:text-[22px]",
                )}
              >
                Is access to Moondream <em>itself</em> the product?
              </span>
              <span className="inline-flex h-[38px] items-center gap-2 rounded-full border border-[#D97706]/30 bg-[#D97706]/10 px-[18px] text-[16px] font-semibold leading-none text-[#B45309] dark:text-[#FBBF24]">
                <Lock className="size-4" strokeWidth={2.5} />
                License required
              </span>
            </div>
          </div>

          <p className="mx-auto mt-7 max-w-[680px] text-center text-[15px] leading-6 text-muted-foreground">
            That is the whole decision. Everything below is detail.
          </p>
        </section>

        <section className="mx-auto max-w-[880px] px-8 pt-24 max-md:px-6 max-md:pt-20">
          <h2 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">
            The single restriction
          </h2>
          <p className="mt-4 text-[16px] leading-[26px] text-muted-foreground">
            In plain terms: you cannot run a hosted service that gives other
            people general-purpose access to Moondream inference or fine-tuning.
            Building domain-specific products on top of Moondream is allowed
            without restriction. The limit applies only when Moondream itself is
            the service you provide to others, whether paid or free, public or
            private.
          </p>

          <div className="mt-6 overflow-hidden rounded-xl border border-[#1F2328] bg-[#06070A]">
            <div className="flex items-center justify-between gap-4 border-b border-[#1F2328] px-4 py-3">
              <span className="mono text-[11px] font-medium uppercase leading-4 tracking-[0.07em] text-[#AEB4BD]">
                Moondream License - Section 3: Hosted Model Service Limitation
              </span>
              <button
                type="button"
                onClick={copyClause}
                className="mono inline-flex items-center gap-1.5 text-[11px] font-medium uppercase leading-4 tracking-[0.06em] text-[#AEB4BD] transition-colors hover:text-white"
                aria-label="Copy license limitation clause"
              >
                {copied ? (
                  <Check className="size-3.5" strokeWidth={2} />
                ) : (
                  <Copy className="size-3.5" strokeWidth={1.75} />
                )}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <pre className="mono m-0 whitespace-pre-wrap break-words p-5 text-[13.5px] leading-6 text-[#F4F5F7]">
              {clauseText}
            </pre>
          </div>

          <p className="mt-3.5 text-[14px] leading-[22px] text-muted-foreground">
            This clause is adapted from the managed-service limitation in the
            Elastic License 2.0, narrowed to general-purpose hosted inference or
            fine-tuning. It is a well-established restriction, not new legal
            language.{" "}
            <Link
              href="#faq"
              className="link text-[var(--text-primary)]"
            >
              More on that below.
            </Link>
          </p>
        </section>

        <section className="mx-auto max-w-[880px] px-8 pt-24 max-md:px-6 max-md:pt-20">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <h2 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">
              The full matrix
            </h2>
            <span className="text-[14px] leading-[22px] text-muted-foreground">
              The same rule, spelled out case by case.
            </span>
          </div>

          <div className="mt-6 overflow-hidden rounded-xl border bg-card">
            <div className="grid grid-cols-1 gap-2 border-b bg-muted px-6 py-3.5 sm:grid-cols-[1fr_auto]">
              <span className="t-eyebrow text-muted-foreground">Use</span>
              <span className="t-eyebrow hidden text-muted-foreground sm:block">
                Status
              </span>
            </div>
            {usageRows.map((row) => (
              <div
                key={row.use}
                className="grid grid-cols-1 items-center gap-3 border-b px-6 py-4 last:border-b-0 sm:grid-cols-[1fr_auto]"
              >
                <span className="text-[15px] leading-[22px]">{row.use}</span>
                <StatusPill tone={row.tone}>{row.status}</StatusPill>
              </div>
            ))}
          </div>
        </section>

        <section
          id="full-text"
          className="mx-auto max-w-[880px] px-8 pt-24 max-md:px-6 max-md:pt-20"
        >
          <h2 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">
            Full license text
          </h2>
          <p className="mt-4 text-[16px] leading-[26px] text-muted-foreground">
            The complete Moondream Model License 1.0. This is the authoritative
            text; the summaries above are for convenience only.
          </p>
          <div className="mt-8 rounded-2xl border bg-card px-8 py-10 max-sm:px-5 max-sm:py-7">
            <article
              className={cn(
                "prose prose-neutral max-w-none dark:prose-invert",
                "prose-headings:scroll-mt-24 prose-headings:font-semibold",
                "prose-h1:text-[30px] prose-h1:leading-tight prose-h1:tracking-[-0.02em]",
                "prose-h2:mt-12 prose-h2:text-[20px] prose-h2:tracking-[-0.01em]",
                "prose-p:text-[15px] prose-p:leading-[25px] prose-li:text-[15px]",
                "prose-table:text-[14px] prose-th:text-left",
              )}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {MODEL_LICENSE_1_0}
              </ReactMarkdown>
            </article>
          </div>
        </section>

        <section
          id="faq"
          className="mx-auto max-w-[880px] px-8 pt-24 max-md:px-6 max-md:pt-20"
        >
          <h2 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">
            Questions
          </h2>
          <div className="mt-6 border-t">
            {faqs.map((item, index) => {
              const isOpen = !!openFaqs[index];
              return (
                <div key={item.q} className="border-b">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenFaqs((current) => ({
                        ...current,
                        [index]: !current[index],
                      }))
                    }
                    className="flex w-full items-center gap-4 px-4 py-[22px] text-left transition-colors hover:bg-muted"
                    aria-expanded={isOpen}
                  >
                    <span className="flex-1 text-[17px] font-semibold leading-[26px] tracking-[-0.01em]">
                      {item.q}
                    </span>
                    <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
                      {isOpen ? (
                        <Minus className="size-5" strokeWidth={1.75} />
                      ) : (
                        <Plus className="size-5" strokeWidth={1.75} />
                      )}
                    </span>
                  </button>
                  {isOpen ? (
                    <div className="max-w-[700px] px-4 pb-6">
                      <p className="text-[16px] leading-[27px] text-muted-foreground">
                        {item.a}
                      </p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>

        <section className="mx-auto max-w-[880px] px-8 pb-32 pt-24 max-md:px-6 max-md:pb-24 max-md:pt-20">
          <div className="flex flex-wrap items-center gap-8 rounded-2xl border bg-card p-10 max-sm:p-6">
            <div className="min-w-[260px] flex-1">
              <h2
                className={cn(
                  serif,
                  "text-[28px] font-medium leading-[34px] tracking-[-0.5px]",
                )}
              >
                Building a hosted Moondream service?
              </h2>
              <p className="mt-3 text-[15px] leading-6 text-muted-foreground">
                If access to Moondream itself is your product, we offer a
                separate commercial license. Tell us what you are building and
                we will scope it.
              </p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setContactOpen(true)}
              >
                Talk to us <ArrowRight className="size-4" strokeWidth={1.75} />
              </button>
              <a href="#full-text" className="btn btn-secondary">
                Read full license{" "}
                <ArrowDown className="size-4" strokeWidth={1.75} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <ContactFormDialog open={contactOpen} onOpenChange={setContactOpen} />
    </>
  );
}
